import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { STORAGE, dispatchPersist, DEFAULT_TABLE_SESSION } from '../constants/storageKeys';
import { normalizePlayerSlots, sessionSlotsHaveNames } from '../utils/tableSession';
import './Scorecard.css';

const POINT_BUTTONS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
const PLAYER_COLORS = [
  '#FF007F',
  '#00F2FE',
  '#FFD700',
  '#B537F2',
  '#FF8200',
  '#00D1FF',
  '#76FF03',
  '#FCE4EC',
];

const PLAYER_ICONS = [
  '🎮',
  '🕹️',
  '🎯',
  '🎲',
  '🃏',
  '🎪',
  '🚀',
  '⭐',
  '🔥',
  '⚡',
  '🌟',
  '💎',
  '🦄',
  '🐉',
  '🦅',
  '🐺',
  '🦁',
  '🐯',
  '👑',
  '🏆',
  '🎊',
  '🎉',
  '🎈',
  '🎭',
  '🌈',
  '🔮',
  '⚔️',
  '🛡️',
  '🏹',
  '🎸',
  '🍕',
  '🍔',
  '🍟',
  '🍩',
  '🍪',
  '🎂',
];

export default function Scorecard() {
  const [gameStarted, setGameStarted] = useState(false);
  const [players, setPlayers] = useState([]);
  const [playerNames, setPlayerNames] = useState(['', '']);
  const [customPoints, setCustomPoints] = useState({});
  const [recentGames, setRecentGames] = useState([]);
  const [gameName, setGameName] = useState('');

  useEffect(() => {
    let kitSession = { ...DEFAULT_TABLE_SESSION };
    try {
      const sr = localStorage.getItem(STORAGE.session);
      if (sr) kitSession = { ...kitSession, ...JSON.parse(sr) };
    } catch {
      /* ignore */
    }

    const savedRecentGames = localStorage.getItem(STORAGE.recentGames);
    if (savedRecentGames) {
      try {
        setRecentGames(JSON.parse(savedRecentGames));
      } catch {
        /* ignore */
      }
    }

    const savedGame = localStorage.getItem(STORAGE.gamePoints);
    if (!savedGame) {
      const gn = String(kitSession.gameName || '').trim().slice(0, 30);
      setGameName(gn);
      setPlayerNames(normalizePlayerSlots(kitSession.players));
      setGameStarted(false);
      setPlayers([]);
      return;
    }

    let gameData;
    try {
      gameData = JSON.parse(savedGame);
    } catch {
      setGameStarted(false);
      setPlayers([]);
      setPlayerNames(normalizePlayerSlots(kitSession.players));
      setGameName(String(kitSession.gameName || '').trim().slice(0, 30));
      return;
    }

    const started = !!gameData.gameStarted;
    setGameStarted(started);
    setPlayers(Array.isArray(gameData.players) ? gameData.players : []);

    const parsedNames = Array.isArray(gameData.playerNames) ? gameData.playerNames : ['', ''];
    const savedGn = gameData.gameName;
    const kitGn = String(kitSession.gameName || '').trim().slice(0, 30);

    if (!started) {
      /* Dashboard (`mt:session`) wins for setup so Hub / First player / Scorecard stay aligned */
      if (sessionSlotsHaveNames(kitSession.players)) {
        setPlayerNames(normalizePlayerSlots(kitSession.players));
      } else {
        const hasAnyName = parsedNames.some((n) => String(n).trim());
        let initialNames = parsedNames;
        if (!hasAnyName && kitSession.players?.length) {
          const sp = kitSession.players.map((s) => String(s).trim()).filter(Boolean).slice(0, 6);
          initialNames = sp.length >= 2 ? sp : [...sp, '', ''].slice(0, 6);
          while (initialNames.length < 2) initialNames.push('');
        }
        setPlayerNames(initialNames);
      }
      setGameName(kitGn || String(savedGn ?? '').trim().slice(0, 30));
    } else {
      setPlayerNames(parsedNames);
      setGameName(String(savedGn ?? '').trim().slice(0, 30));
    }
  }, []);

  useEffect(() => {
    const gameData = {
      gameStarted,
      players,
      playerNames,
      gameName,
    };
    localStorage.setItem(STORAGE.gamePoints, JSON.stringify(gameData));

    const roster = gameStarted
      ? players.map((p) => String(p.name || '').trim()).filter(Boolean)
      : playerNames.map((n) => String(n).trim()).filter(Boolean);
    try {
      localStorage.setItem(
        STORAGE.session,
        JSON.stringify({
          gameName: String(gameName || '').trim().slice(0, 30),
          players: roster.slice(0, 6).map((n) => n.slice(0, 20)),
        })
      );
      dispatchPersist(STORAGE.session);
    } catch {
      /* quota / private mode */
    }
  }, [gameStarted, players, playerNames, gameName]);

  const addPlayer = () => {
    if (playerNames.length < 6) {
      setPlayerNames([...playerNames, '']);
    }
  };

  const removePlayer = (index) => {
    if (playerNames.length > 2) {
      const newNames = playerNames.filter((_, i) => i !== index);
      setPlayerNames(newNames);
    }
  };

  const updatePlayerName = (index, name) => {
    const newNames = [...playerNames];
    newNames[index] = name;
    setPlayerNames(newNames);

    if (name.length === 1 && index === playerNames.length - 1 && playerNames.length < 6) {
      setPlayerNames([...newNames, '']);
    }
  };

  const startGame = () => {
    const validNames = playerNames.filter((name) => name.trim() !== '');
    if (validNames.length >= 2) {
      const shuffledIcons = [...PLAYER_ICONS].sort(() => Math.random() - 0.5);

      const initialPlayers = validNames.map((name, index) => ({
        id: index,
        name: name.trim(),
        score: 0,
        color: PLAYER_COLORS[index % PLAYER_COLORS.length],
        icon: shuffledIcons[index % shuffledIcons.length],
      }));
      setPlayers(initialPlayers);
      setGameStarted(true);
    }
  };

  const saveGameToRecent = () => {
    if (players.length > 0) {
      const currentWinners = getWinners();
      const gameSnapshot = {
        id: Date.now(),
        date: new Date().toLocaleDateString(),
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        players: players.map((p) => ({ name: p.name, score: p.score, color: p.color, icon: p.icon })),
        winners: currentWinners,
        winner: currentWinners[0],
        gameName,
      };

      const updatedRecentGames = [gameSnapshot, ...recentGames.slice(0, 4)];
      setRecentGames(updatedRecentGames);
      localStorage.setItem(STORAGE.recentGames, JSON.stringify(updatedRecentGames));
    }
  };

  const loadRecentGame = (gameSnapshot) => {
    const restoredPlayers = gameSnapshot.players.map((p, index) => ({
      id: index,
      name: p.name,
      score: p.score,
      color: p.color,
      icon: p.icon || '🎮',
    }));

    setPlayers(restoredPlayers);
    setPlayerNames(gameSnapshot.players.map((p) => p.name));
    setGameName(gameSnapshot.gameName || '');
    setGameStarted(true);
    setCustomPoints({});
  };

  const resetGame = () => {
    if (gameStarted && players.length > 0) {
      saveGameToRecent();
    }

    const currentNames =
      players.length > 0 ? players.map((p) => p.name) : playerNames.filter((name) => name.trim() !== '');

    setGameStarted(false);
    setPlayers([]);
    setPlayerNames(currentNames.length >= 2 ? currentNames : ['', '']);
    setCustomPoints({});
    localStorage.removeItem(STORAGE.gamePoints);
  };

  const updateScore = (playerId, points) => {
    setPlayers(
      players.map((player) =>
        player.id === playerId ? { ...player, score: Math.max(0, player.score + points) } : player
      )
    );
  };

  const handleCustomPointsChange = (playerId, value) => {
    setCustomPoints({
      ...customPoints,
      [playerId]: value,
    });
  };

  const applyCustomPoints = (playerId, isPositive = true) => {
    const points = parseInt(customPoints[playerId] || 0, 10);
    if (points > 0) {
      updateScore(playerId, isPositive ? points : -points);
      setCustomPoints({
        ...customPoints,
        [playerId]: '',
      });
    }
  };

  const getWinners = () => {
    if (players.length === 0) return [];
    const maxScore = Math.max(...players.map((p) => p.score));
    return players.filter((player) => player.score === maxScore);
  };

  if (!gameStarted) {
    return (
      <div className="scorecard-screen">
        <div className="setup-container">
          <Link to="/" className="kit-back-link">
            ← Game Night Kit
          </Link>
          <div className="header-container">
            <img src={`${process.env.PUBLIC_URL || ''}/favicon.png`} alt="Mascot" className="mascot-small" />
            <h1 className="title">Scorecard</h1>
          </div>
          <p className="subtitle">Enter player names to start tracking points!</p>

          <div className="game-name-setup">
            <input
              type="text"
              placeholder="Game name (optional)"
              value={gameName}
              onChange={(e) => setGameName(e.target.value)}
              className="game-name-input"
              maxLength={30}
            />
          </div>

          <div className="player-setup">
            {playerNames.map((name, index) => (
              <div key={index} className="player-input-group">
                <input
                  type="text"
                  placeholder={`Player ${index + 1} name`}
                  value={name}
                  onChange={(e) => updatePlayerName(index, e.target.value)}
                  className="player-name-input"
                  maxLength={20}
                />
                {playerNames.length > 2 && (
                  <button type="button" onClick={() => removePlayer(index)} className="remove-player-btn" aria-label="Remove player">
                    ❌
                  </button>
                )}
              </div>
            ))}

            {playerNames.length < 6 && (
              <button type="button" onClick={addPlayer} className="add-player-btn">
                ➕ Add Player
              </button>
            )}
          </div>

          <button
            type="button"
            onClick={startGame}
            className="start-game-btn"
            disabled={playerNames.filter((name) => name.trim() !== '').length < 2}
          >
            🚀 Start Game!
          </button>
        </div>
      </div>
    );
  }

  const winners = getWinners();
  const topScore = winners.length > 0 ? winners[0].score : 0;

  return (
    <div className="scorecard-screen">
      {winners.length > 0 && topScore > 0 && (
        <div
          className="winner-banner"
          style={{
            backgroundColor: `${winners[0].color}20`,
            borderColor: winners[0].color,
            background:
              winners.length > 1
                ? `linear-gradient(90deg, ${winners.map((w) => `${w.color}20`).join(', ')})`
                : `${winners[0].color}20`,
          }}
        >
          🏆{' '}
          {winners.length === 1
            ? `${winners[0].icon} ${winners[0].name} is leading with ${topScore} points`
            : `${winners.map((w) => `${w.icon} ${w.name}`).join(', ')} are tied with ${topScore} points`}
          {gameName ? ` in ${gameName}` : ''}!
        </div>
      )}

      <div className="players-grid">
        {players.map((player) => (
          <div key={player.id} className="player-card" style={{ borderColor: player.color }}>
            <div className="player-header" style={{ backgroundColor: player.color }}>
              <div className="player-info">
                <span className="player-name">
                  <span className="player-icon">{player.icon}</span>
                  {player.name}
                </span>
                <span className="player-score">{player.score}</span>
              </div>
            </div>

            <div className="scoring-section">
              <div className="points-grid">
                <div className="points-row">
                  <span className="points-label">Add:</span>
                  {POINT_BUTTONS.slice(0, 5).map((points) => (
                    <button
                      type="button"
                      key={points}
                      onClick={() => updateScore(player.id, points)}
                      className="point-btn positive"
                    >
                      +{points}
                    </button>
                  ))}
                </div>
                <div className="points-row">
                  <span className="points-label" />
                  {POINT_BUTTONS.slice(5, 10).map((points) => (
                    <button
                      type="button"
                      key={points}
                      onClick={() => updateScore(player.id, points)}
                      className="point-btn positive"
                    >
                      +{points}
                    </button>
                  ))}
                </div>
              </div>

              <div className="custom-points">
                <div className="custom-input-group">
                  <input
                    type="number"
                    placeholder="99"
                    value={customPoints[player.id] || ''}
                    onChange={(e) => handleCustomPointsChange(player.id, e.target.value)}
                    className="custom-points-input"
                    min="1"
                    max="99"
                    maxLength="2"
                  />
                  <button
                    type="button"
                    onClick={() => applyCustomPoints(player.id, true)}
                    className="custom-btn positive"
                    disabled={!customPoints[player.id] || customPoints[player.id] <= 0}
                  >
                    ➕
                  </button>
                  <button
                    type="button"
                    onClick={() => applyCustomPoints(player.id, false)}
                    className="custom-btn negative"
                    disabled={!customPoints[player.id] || customPoints[player.id] <= 0}
                  >
                    ➖
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="bottom-panel">
        <div className="game-header">
          <div className="footer-header">
            <img
              src={`${process.env.PUBLIC_URL || ''}/favicon.png`}
              alt="Mascot"
              className="mascot-small"
              style={{ width: '32px', height: '32px' }}
            />
            <h1 className="game-title">Scorecard</h1>
          </div>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <Link to="/" className="kit-back-link" style={{ alignSelf: 'center' }}>
              ← Kit
            </Link>
            <button type="button" onClick={resetGame} className="reset-btn">
              🔄 New Game
            </button>
          </div>
        </div>

        {recentGames.length > 0 && (
          <div className="recent-games">
            <h3 className="recent-games-title">Recent Games</h3>
            <div className="recent-games-list">
              {recentGames.map((game) => (
                <div
                  key={game.id}
                  className="recent-game-item"
                  onClick={() => loadRecentGame(game)}
                  onKeyDown={(e) => e.key === 'Enter' && loadRecentGame(game)}
                  role="button"
                  tabIndex={0}
                >
                  <div className="recent-game-info">
                    <div className="recent-game-date">
                      {game.date} {game.time}
                    </div>
                    <div className="recent-game-winner">
                      🏆{' '}
                      {game.winners && game.winners.length > 1
                        ? `${game.winners.map((w) => `${w.icon} ${w.name}`).join(', ')} tied (${game.winners[0].score})`
                        : `${game.winner.icon ? `${game.winner.icon} ` : ''}${game.winner.name} (${game.winner.score})`}
                      {game.gameName ? ` - ${game.gameName}` : ''}
                    </div>
                  </div>
                  <div className="recent-game-players">
                    {game.players.map((player, index) => (
                      <span key={index} className="recent-player" style={{ color: player.color }}>
                        {player.icon ? `${player.icon} ` : ''}
                        {player.name}: {player.score}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
